import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CreditCard } from "@phosphor-icons/react";

export default function TenantPlan() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/plan").then((r) => { setData(r.data); setSel(r.data.active_plan); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/admin/plan", { plan_id: sel });
      toast.success(`Plan set to ${res.plan_name} · limit ${res.registration_limit || "∞"} registrations. No payment taken.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update the plan.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;
  const dirty = sel !== data.active_plan;

  return (
    <div className="border border-border bg-card p-6 max-w-2xl mb-6" data-testid="tenant-plan">
      <div className="flex items-start gap-3">
        <CreditCard size={22} className="mt-0.5 text-primary shrink-0" />
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg">Tenant plan</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Manually assign this tenant's plan for sales-assisted provisioning. Sets the feature tier and
            registration limit — no payment is taken.
          </p>
          <div className="mt-3 text-sm">
            Current: <span data-testid="current-plan" className="font-semibold">{data.plan_name}</span>
            {" · limit "}{data.registration_limit || "∞"}{" · using "}{data.registrations_used}
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-sm font-medium">Plan</label>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="plan-select" className="rounded-none mt-1 w-80"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.plans.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`plan-option-${p.id}`}>
                  {p.name}{p.contact_only ? " (custom)" : ` — ${p.registration_limit || "∞"} regs`}
                  {p.applicability_locked ? " · applicability-locked" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="save-plan" onClick={save} disabled={saving || !dirty}
          className="rounded-none bg-primary hover:bg-primary/90">{saving ? "Saving…" : "Assign plan"}</Button>
      </div>
    </div>
  );
}
